import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { useStyles } from './SessionNote.styles';
import { useNavigate } from 'react-router-dom';
import Editor from 'react-simple-wysiwyg';
import { getProfile } from '../../../../../../redux/modules/profile/selectors';
import { ProfileInfoRole } from '../../../../../../components/v2/ProfileInfo';

const SessionNote = ({ appointment, text, handleChange }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector(getProfile);

  const isEmptyContent = content =>
    !content ||
    content === '<p><br></p>' ||
    content === '<p></p>' ||
    content.trim() === '' ||
    content.replace(/<[^>]*>/g, '').trim() === '';

  const getDefaultTemplate = () => `<h3 style="color: #222222; margin:0;">Summary</h3>
    <p style="color: #888888; font-style: italic; margin:0;">Please enter a summary of the session, including key topics discussed and progress made.</p>
    <br/>
    <h3 style="color: #222222; margin:0;">Subjective</h3>
    <p style="color: #888888; font-style: italic; margin:0;">Document the client's self-reported concerns, symptoms, or experiences shared during the session.</p>
    <br/>
    <h3 style="color: #222222; margin:0;">Objective</h3>
    <p style="color: #888888; font-style: italic; margin:0;">Record observable behaviors, clinical impressions, and any relevant mental status findings.</p>
    <br/>
    <h3 style="color: #222222; margin:0;">Assessment</h3>
    <p style="color: #888888; font-style: italic; margin:0;">Summarize clinical impressions based on the session.</p>
    <br/>
    <h3 style="color: #222222; margin:0;">Plan</h3>
    <p style="color: #888888; font-style: italic; margin:0;">Outline any updates or adjustments based on this session.</p>`;

  const getCareManagerTemplate =
    () => `<h3 style="color: #222222; margin: 0; font-size: 16px; font-weight: bold;">Data</h3>
    <p style="color: #888888; font-style: italic; margin: 0; font-size: 14px;">Describe the care-navigation interaction: where/how the session occurred, concerns addressed, and key details about history, services, barriers, supports, or interventions (e.g., resource navigation, motivational interviewing).</p>
    <br>
    <h3 style="color: #222222; margin: 0; font-size: 16px; font-weight: bold;">Assessment</h3>
    <p style="color: #888888; font-style: italic; margin: 0; font-size: 14px;">Summarize your impression of the patient's needs, motivation, risk, and any impairments or supports influencing care.</p>
    <br>
    <h3 style="color: #222222; margin: 0; font-size: 16px; font-weight: bold;">Plan</h3>
    <p style="color: #888888; font-style: italic; margin: 0; font-size: 14px;">Outline follow-up steps, referrals, shared goals, safety plans, or coordination activities. Include the date/time of any next scheduled contact.</p>`;

  const getTemplateText = () => {
    if (profile?.designation === ProfileInfoRole.CARE_MANAGER) return getCareManagerTemplate();
    return getDefaultTemplate();
  };

  useEffect(() => {
    if (isEmptyContent(text)) {
      handleChange(getTemplateText());
    }
  }, []);

  const handleEditorChange = e => {
    // Always process the content, don't return early
    if (isEmptyContent(e.target.value)) {
      handleChange(getTemplateText());
    } else {
      handleChange(e.target.value);
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <span>Session note</span>
        <span className={classes.headerSubTitle}>
          Please update the session note we generated for you to best fit your style.
        </span>
      </Box>
      <Box className={classes.body}>
        <Editor value={text} onChange={handleEditorChange} />
        {appointment?.waitingForAI && (
          <Box className={classes.blurOverlay} />
        )}
      </Box>
    </Box>
  );
};
export { SessionNote };
